import {
  faBatteryLow,
  faBellSlash,
  faBolt,
  faCircleBolt,
  faClock,
  faClockRotateLeft,
  faCloudArrowUp,
  faCloudXmark,
  faSignalStreamSlash,
  faTemperatureArrowDown,
  faTemperatureArrowUp,
  faTriangleExclamation,
  faUserClock,
} from '@fortawesome/pro-solid-svg-icons';

// Category describes how a metric's value should be rendered and whether it is
// "drillable" (clicking opens a modal listing the underlying items).
export const METRIC_CATEGORY = {
  COUNT: 'count',
  TEXT: 'text',
  TIMESTAMP: 'timestamp',
  TEMP: 'temp',
};

// The list of items a drillable count metric expands into.
export const DRILL_SOURCE = {
  ALERTS: 'alerts',
  DEVICES: 'devices',
  SENSORS: 'sensors',
  RECIPIENTS: 'recipients',
};

// Ordered list of every metric the Statistics widget can display.
// `key`          - stable identifier persisted in widget context. This MUST match
//                  the backend `StatisticType` enum name so the edit payload is
//                  understood by the server without translation.
// `label`        - text shown in the "Metric" column and Edit checkbox.
// `icon`         - FontAwesome icon for the row.
// `color`        - CSS color for the icon.
// `category`     - drives value formatting in the View.
// `drillSource`  - when set, the count is clickable and opens a list modal.
export const STATISTICS_METRICS = [
  {
    key: 'DEVICES_PUSHING',
    label: 'Devices Pushing',
    icon: faCloudArrowUp,
    color: '#2f7fd1',
    category: METRIC_CATEGORY.COUNT,
    drillSource: DRILL_SOURCE.DEVICES,
  },
  {
    key: 'DEVICES_NOT_PUSHING',
    label: 'Devices Not Pushing',
    icon: faCloudXmark,
    color: '#d13d3d',
    category: METRIC_CATEGORY.COUNT,
    drillSource: DRILL_SOURCE.DEVICES,
  },
  {
    key: 'ALERTS_TRIGGERED',
    label: 'Alerts Triggered',
    icon: faBolt,
    color: '#d13d3d',
    category: METRIC_CATEGORY.COUNT,
    drillSource: DRILL_SOURCE.ALERTS,
  },
  {
    key: 'ALERTS_ACKNOWLEDGED',
    label: 'Alerts Acknowledged',
    icon: faBellSlash,
    color: '#e08a2b',
    category: METRIC_CATEGORY.COUNT,
    drillSource: DRILL_SOURCE.ALERTS,
  },
  {
    key: 'LAST_TRIGGERED_ALERT',
    label: 'Last Triggered Alert',
    icon: faClockRotateLeft,
    color: '#e0c040',
    category: METRIC_CATEGORY.TEXT,
  },
  {
    key: 'LAST_ALERT_TIMESTAMP',
    label: 'Last Alert Timestamp',
    icon: faClock,
    color: '#e0c040',
    category: METRIC_CATEGORY.TIMESTAMP,
  },
  {
    key: 'SENSORS_LOW_BATTERY',
    label: 'Sensors with Low Battery',
    icon: faBatteryLow,
    color: '#d13d3d',
    category: METRIC_CATEGORY.COUNT,
    drillSource: DRILL_SOURCE.SENSORS,
  },
  {
    key: 'SENSORS_NO_WIRELESS_SIGNAL',
    label: 'Sensors with No Wireless Signal',
    icon: faSignalStreamSlash,
    color: '#d13d3d',
    category: METRIC_CATEGORY.COUNT,
    drillSource: DRILL_SOURCE.SENSORS,
  },
  {
    key: 'DEVICES_OLD_FIRMWARE',
    label: 'Devices Running Old Firmware',
    icon: faTriangleExclamation,
    color: '#e08a2b',
    category: METRIC_CATEGORY.COUNT,
    drillSource: DRILL_SOURCE.DEVICES,
  },
  {
    key: 'HIGHEST_CURRENT_TEMP',
    label: 'Highest Current Temp',
    icon: faTemperatureArrowUp,
    color: '#d13d3d',
    category: METRIC_CATEGORY.TEMP,
  },
  {
    key: 'LOWEST_CURRENT_TEMP',
    label: 'Lowest Current Temp',
    icon: faTemperatureArrowDown,
    color: '#2f7fd1',
    category: METRIC_CATEGORY.TEMP,
  },
  {
    key: 'MOST_FREQUENTLY_TRIGGERED_ALERT',
    label: 'Alert Triggered Most Frequently',
    icon: faCircleBolt,
    color: '#e0c040',
    category: METRIC_CATEGORY.TEXT,
  },
  {
    key: 'MOST_FREQUENTLY_NOTIFIED_USER',
    label: 'User Notified Most Frequently',
    icon: faUserClock,
    color: '#e0c040',
    category: METRIC_CATEGORY.TEXT,
  },
];

export const STATISTICS_METRICS_BY_KEY = STATISTICS_METRICS.reduce((acc, metric) => {
  acc[metric.key] = metric;
  return acc;
}, {});

// Set of valid backend `StatisticType` enum names, useful for validating payloads.
export const VALID_STATISTIC_TYPES = new Set(STATISTICS_METRICS.map((m) => m.key));

// Default selection mirrors the wireframe: the first few summary metrics.
export const DEFAULT_SELECTED_METRICS = [
  'DEVICES_PUSHING',
  'ALERTS_TRIGGERED',
  'LAST_ALERT_TIMESTAMP',
];
